import configuration from 'utils/configuration';
import fieldLabelKeyMap from 'utils/field-label-map';

export async function setLocale(locale) {
  const app = this;

  // Only skip if locale matches AND messages already exist
  const existing = app.$i18n.getLocaleMessage?.(locale);
  const hasExisting = existing && Object.keys(existing).length > 0;
  if (app.$i18n.locale === locale && hasExisting) {
    return;
  }

  try {
    const url = app.$hrefBase + "resources?v=" + (Math.random() + 1).toString().slice(2);
    const raw = await configuration.loadLocaleMessages(url);

    // Build a validation subtree for vee-validate from flat keys:
    // - validation:messages:vee-validate:<rule>
    // - validation:messages:vee-validate:custom:<field>:<rule>
    const validationMessages = {};
    const validationCustom = {};

    Object.entries(raw).forEach(([key, value]) => {
      const val = value == null ? '' : String(value);

      if (key.startsWith('validation:messages:vee-validate:custom:')) {
        // custom:<field>:<rule>
        // e.g., validation:messages:vee-validate:custom:answer0:regex
        const parts = key.split(':'); // ['validation','messages','vee-validate','custom','<field>','<rule>']
        const field = parts[4];
        const rule = parts[5];
        if (field && rule) {
          validationCustom[field] ??= {};
          validationCustom[field][rule] = val;
        }
      } else if (key.startsWith('validation:messages:vee-validate:')) {
        // standard rule message
        // e.g., validation:messages:vee-validate:required
        const rule = key.substring('validation:messages:vee-validate:'.length);
        if (rule) {
          validationMessages[rule] = val;
        }
      }
    });

    // Precompile: wrap every message in a function so vue-i18n does NOT need the runtime compiler.
    // Keep flat colon-delimited keys intact.
    const messagesFunc = Object.fromEntries(
      Object.entries(raw).map(([k, v]) => [k, () => (v == null ? '' : String(v))])
    );

    // Attach the validation subtree (plain strings) so vee-validate can read templates via i18n.global.getLocaleMessage(locale).validation...
    // This does not affect $t('...') lookups; those use the flat function messages above.
    const localePayload = {
      ...messagesFunc,
      validation: {
        messages: validationMessages,
        custom: validationCustom
      }
    };

    app.$i18n.setLocaleMessage(locale, localePayload);
    app.$i18n.locale = locale;
  } catch (error) {
    console.log(error);
  }
}

// Function to handle dynamic field names
function resolveFieldLabelKey(fieldName) {
  if (/^(sn)?answer\d+$/.test(fieldName)) {
    return fieldLabelKeyMap.Answer;
  }
  return fieldLabelKeyMap[fieldName] || fieldName;
}

function readLocaleValue(localeMessage, key) {
  if (!localeMessage) {
    return null;
  }

  const direct = localeMessage[key];
  if (typeof direct === 'function') {
    return direct();
  }
  if (typeof direct === 'string') {
    return direct;
  }

  // Optional nested fallback for validation subtree
  if (key.startsWith('validation:messages:vee-validate:custom:')) {
    const parts = key.split(':'); // validation:messages:vee-validate:custom:<field>:<rule>
    const field = parts[4];
    const rule = parts[5];
    const nested = localeMessage.validation?.custom?.[field]?.[rule];
    return typeof nested === 'string' ? nested : null;
  }

  if (key.startsWith('validation:messages:vee-validate:')) {
    const rule = key.substring('validation:messages:vee-validate:'.length);
    const nested = localeMessage.validation?.messages?.[rule];
    return typeof nested === 'string' ? nested : null;
  }

  return null;
}

function getLocalizedValue(i18n, key) {
  const global = i18n.global;
  const currentLocale = typeof global.locale === 'string' ? global.locale : global.locale?.value;
  const fallbackRaw = typeof global.fallbackLocale === 'string' ? global.fallbackLocale : global.fallbackLocale?.value;
  const fallbackLocales = Array.isArray(fallbackRaw) ? fallbackRaw : (fallbackRaw ? [fallbackRaw] : []);

  const currentMessage = global.getLocaleMessage(currentLocale);
  const fromCurrent = readLocaleValue(currentMessage, key);
  if (fromCurrent != null) {
    return fromCurrent;
  }

  for (const fb of fallbackLocales) {
    const fbMessage = global.getLocaleMessage(fb);
    const fromFallback = readLocaleValue(fbMessage, key);
    if (fromFallback != null) {
      return fromFallback;
    }
  }

  return null;
}

// Translate only mapped resource keys; keep literal labels as-is.
function resolveFieldDisplayLabel(i18n, fieldName) {
  const mappedKey = resolveFieldLabelKey(fieldName);

  // If mapping changed, it's a resource key candidate.
  if (mappedKey !== fieldName) {
    return getLocalizedValue(i18n, mappedKey) ?? String(fieldName ?? '');
  }

  return String(fieldName ?? '');
}

// Use field name token for custom-key lookup when available.
function resolveFieldToken(ctx) {
  return ctx?.name || ctx?.field || '';
}

export function generateValidationMessage(i18n, ctx) {
  const ruleName = ctx.rule?.name || '_default';
  const fieldToken = resolveFieldToken(ctx);

  const label = resolveFieldDisplayLabel(i18n, ctx.field);

  const customKey = `validation:messages:vee-validate:custom:${fieldToken}:${ruleName}`;
  const baseKey = `validation:messages:vee-validate:${ruleName}`;
  const defaultKey = 'validation:messages:vee-validate:_default';

  // Silent lookup: no missing-key warnings
  const template =
    getLocalizedValue(i18n, customKey) ??
    getLocalizedValue(i18n, baseKey) ??
    getLocalizedValue(i18n, defaultKey) ??
    defaultKey;

  const rawParams = Array.isArray(ctx.rule?.params) ? [...ctx.rule.params] : [];

  // confirmed: allow explicit display label as 2nd param
  if (ruleName === 'confirmed' && rawParams.length > 1) {
    rawParams[0] = rawParams[1];
  }

  const args = [label, ...rawParams];

  return String(template).replace(/\{(\d+)\}/g, (_, idx) => {
    const i = Number(idx);
    const value = args[i];
    return value == null ? '' : String(value);
  }).trim();
}